import { type ShareDocument, shareFileName } from '@escala/domain/share';

import { loadFonts, loadIcons, loadPhotos } from './assets';
import { COLORS, canvasBlob, createCanvas } from './canvas';
import type { ShareFile, ShareOptions } from './image';
import {
  CARD_GAP,
  COMPACT_HEADER,
  COMPACT,
  type Card,
  FOOTER,
  MARGIN,
  type Piece,
  type RenderContext,
  WIDTH,
  buildCards,
  cardHeight,
  drawCard,
  drawCompactHeader,
  drawFooter,
  drawHeader,
  measureHeader,
} from './layout';
import { A4, buildPdf } from './pdfWriter';

/**
 * A escala em PDF (A4): o mesmo desenho da imagem, repartido em páginas. A primeira
 * leva o cabeçalho completo; as seguintes, um cabeçalho enxuto. Um card que não cabe
 * no resto da página vai para a próxima; se nem numa página inteira couber, quebra entre
 * as funções e continua na seguinte com "continuação" (e o cabeçalho do ministério, se a
 * quebra caiu no meio dele). Cada página é desenhada em canvas
 * e entra no PDF como JPEG — por isso o texto não é selecionável.
 */

const PAGE_HEIGHT = Math.round((WIDTH * A4.height) / A4.width);
const SCALE = 2;

export interface PdfSegment {
  pieces: Piece[];
}

/** Reparte os cards entre as páginas: `first` e `other` são a altura útil de cada uma. */
export function paginate(cards: readonly Card[], first: number, other: number): PdfSegment[][] {
  const pages: PdfSegment[][] = [[]];
  let used = 0;
  let room = first;

  const newPage = () => {
    pages.push([]);
    used = 0;
    room = other;
  };
  const current = () => pages[pages.length - 1]!;

  for (const card of cards) {
    let pieces = card.pieces;
    let continued = false;

    while (pieces.length) {
      const segment = continued ? [card.continued, ...pieces] : pieces;
      const gap = current().length ? CARD_GAP : 0;
      const height = cardHeight(segment);

      if (used + gap + height <= room) {
        current().push({ pieces: segment });
        used += gap + height;
        break;
      }

      // Cabe numa página inteira: vai para a próxima, sem quebrar.
      if (current().length && height <= other) {
        newPage();
        continue;
      }

      // Quebra: o maior começo que cabe, sem terminar num cabeçalho de ministério e com
      // pelo menos uma função.
      let take = segment.length - 1;

      while (take > 0 && used + gap + cardHeight(segment.slice(0, take)) > room) take--;
      while (take > 0 && segment[take - 1]!.kind === 'section') take--;

      if (!segment.slice(0, take).some((p) => p.kind === 'row')) {
        if (current().length) {
          newPage();
          continue;
        }

        // Página vazia e nem a primeira função cabe: leva até a primeira função assim mesmo.
        take = segment.findIndex((p) => p.kind === 'row') + 1 || segment.length;
      }

      const taken = segment.slice(0, take);
      const section = taken.findLast((p) => p.kind === 'section');

      current().push({ pieces: taken });
      pieces = segment.slice(take);

      // A quebra caiu no meio de um ministério: o cabeçalho dele abre a continuação.
      if (section && pieces[0]?.kind === 'row') pieces = [section, ...pieces];

      continued = true;
      newPage();
    }
  }

  return pages.filter((page) => page.length);
}

function drawPage(
  doc: ShareDocument,
  rc: RenderContext,
  segments: readonly PdfSegment[],
  index: number,
  total: number
): HTMLCanvasElement {
  const { canvas, ctx } = createCanvas(WIDTH, PAGE_HEIGHT, SCALE);

  ctx.fillStyle = COLORS.page;
  ctx.fillRect(0, 0, WIDTH, PAGE_HEIGHT);

  let y = index === 1 ? drawHeader(ctx, doc, rc) + 36 : COMPACT_HEADER + 20;

  if (index > 1) drawCompactHeader(ctx, doc, rc);

  for (const segment of segments) {
    drawCard(ctx, segment.pieces, MARGIN, y, WIDTH - MARGIN * 2);
    y += cardHeight(segment.pieces) + CARD_GAP;
  }

  drawFooter(ctx, PAGE_HEIGHT - FOOTER, rc, { index, total });

  return canvas;
}

/** O PDF e as páginas em JPEG (a prévia mostra as mesmas páginas do arquivo). */
export async function renderSharePdf(
  doc: ShareDocument,
  options: ShareOptions
): Promise<{ pdf: ShareFile; pages: Blob[] }> {
  await loadFonts();
  const rc: RenderContext = {
    org: options.org,
    color: options.color,
    generatedAt: options.generatedAt,
    photos: options.photos ? await loadPhotos(doc) : new Map(),
    icons: await loadIcons(doc, options.iconSvg),
  };
  const { ctx: measure } = createCanvas(1, 1, 1);
  const cards = buildCards(measure, doc, rc, COMPACT);
  const bottom = FOOTER + 8;
  const pages = paginate(
    cards,
    PAGE_HEIGHT - measureHeader(measure, doc, rc) - 36 - bottom,
    PAGE_HEIGHT - COMPACT_HEADER - 20 - bottom
  );
  const jpegs = await Promise.all(
    pages.map((segments, i) =>
      canvasBlob(drawPage(doc, rc, segments, i + 1, pages.length), 'image/jpeg', 0.9)
    )
  );
  const bytes = buildPdf(
    await Promise.all(
      jpegs.map(async (blob) => ({
        jpeg: new Uint8Array(await blob.arrayBuffer()),
        width: WIDTH * SCALE,
        height: PAGE_HEIGHT * SCALE,
      }))
    ),
    doc.title
  );

  return {
    pdf: { name: `${shareFileName(doc)}.pdf`, blob: new Blob([bytes], { type: 'application/pdf' }) },
    pages: jpegs,
  };
}
